/* eslint-disable sonarjs/no-duplicate-string */
/** * *
 * Component: Insights
 * Date:1/12/2021
 * */

import React, { useRef, useMemo, useEffect } from 'react';
import { Filter, Graphs } from 'containers/index';
import moment from 'moment';
import { useQueryClient, useMutation, useQuery } from 'react-query';
import { LiveDataContext } from 'context/liveData';
import _ from 'lodash';

import { fetchGraphData, getChannelAgentMapping } from './api';
import {
  dateRangeFilterConfig,
  groupsFilterConfig,
  initialSelectedFilter,
} from './filterConfig';
import styles from './style.module.css';

const Insights = (): JSX.Element => {
  const queryClient = useQueryClient();
  const graphWindowRef = useRef<any>();
  const mainContainer = useRef<any>();
  const { subscribedAgents } = LiveDataContext();

  /**
   * append the groups filter into the filters list for Filter component.
   * @param res: Object of channel_agent_mapping.
   *  */
  const modifyInitiateFiltersMapping = (res: any) => {
    if (res) {
      let data: any = {
        displayFilters: [dateRangeFilterConfig],
      };
      if (res?.groupNames)
        data = {
          groupsName: res?.groupNames || [],
          displayFilters: [dateRangeFilterConfig, groupsFilterConfig],
        };
      return data;
    }
    return res;
  };

  const { data: channelAgentMapData } = useQuery(
    'insightsGroupMapping',
    getChannelAgentMapping,
    {
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );

  /**
   *  Modify filters list based on API response for channel agent mapping API.
   *  */
  const filters = useMemo(
    () => modifyInitiateFiltersMapping(channelAgentMapData),
    [channelAgentMapData],
  );

  const cachedSelectedFilters = useMemo(
    () =>
      (queryClient.getQueryData('cachedFiltersData') as any)?.selectedFilters ||
      initialSelectedFilter,
    [queryClient.getQueryData('cachedFiltersData')],
  );
  const cachedGraphData = useMemo(
    () =>
      (queryClient.getQueryData('cachedFiltersData') as any)?.graphData || {},
    [queryClient.getQueryData('cachedFiltersData')],
  );

  /**
   *  Fill graph as per the filter applied via API call and prep the cache.
   *  */
  const { isLoading: resultsLoading, mutate: fetchTrends } = useMutation(
    'populateFigures',
    (selectedFilters: any[]) =>
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      fetchGraphData(onFilterAppliedParser(selectedFilters)),
    {
      onSuccess: (data, variables) => {
        if (!_.isEmpty(data)) {
          queryClient.setQueryData('cachedFiltersData', {
            graphData: data,
            selectedFilters: variables,
          });
        }
      },
      onError: (err) => {
        console.log('Error', err);
      },
    },
  );

  /**
   * Parse the applied filters into suitable request object.
   * @param newFilter {Array} array on object of filters applied.
   *  */
  const onFilterAppliedParser = (newFilter: any[]) => {
    // If date not selected set default dates.
    const requestObj: any = {
      dateRange: {
        startDate: '',
        endDate: '',
      },
    };
    newFilter.forEach((filter: any) => {
      switch (filter.type) {
        case 'multiTree': {
          requestObj.agentList = filter?.value || [];
          break;
        }
        case 'date': {
          if (filter.condition === 'between') {
            requestObj.dateRange = {
              startDate: filter.value[0],
              endDate: filter.value[1],
            };
          }
          if (filter.condition === 'equals') {
            const startDateArg = moment();
            const endDateArg = moment();
            // For getting all results
            // set end date as today and start sate as empty to BE.
            if (filter.value === '-1') {
              requestObj.dateRange = {
                startDate: '',
                endDate: '',
              };
            } else {
              startDateArg.subtract(filter.value, 'd');
              requestObj.dateRange = {
                startDate: startDateArg.format('YYYY-MM-DDTHH:mm:ss'),
                endDate: endDateArg.format('YYYY-MM-DDTHH:mm:ss'),
              };
            }
          }
          break;
        }
        default:
      }
    });
    return requestObj;
  };

  /**
   * Once we have filters data cached along with results, prevent recalling API.
   *  used for caching during tab switched.
   *  */
  useEffect(() => {
    const cachedData = queryClient.getQueryData('cachedFiltersData') as any;
    if (!cachedData?.graphData)
      if (cachedData?.selectedFilter) {
        fetchTrends(cachedData.selectedFilter);
      } else {
        let filterObj: any[] = initialSelectedFilter;
        // subscribeAgents is an array from context
        if (subscribedAgents?.length) {
          filterObj = [
            ...filterObj,
            {
              field: 'groupsName',
              condition: 'equals',
              value: subscribedAgents,
              type: 'multiTree',
            },
          ];
          fetchTrends(filterObj);
        }
      }
  }, [subscribedAgents]);

  return (
    <div className={styles.insightsContainer}>
      <div className={styles.filterContainer}>
        <Filter
          skeletonLoaders="reviewFilter"
          selectedValues={cachedSelectedFilters}
          filters={filters}
          onSearchClick={(newFilters: any[]) => {
            fetchTrends(newFilters);
          }}
          filterChanged={false}
          filterLimit={2}
          resultsLoading={resultsLoading}
          disableButton
          isSmartReport={false}
          searchPage={false}
        />
      </div>
      <div className={styles.trendsContainer} ref={mainContainer}>
        <Graphs
          state={cachedGraphData}
          containerRef={graphWindowRef}
          mainRef={mainContainer}
        />
      </div>
    </div>
  );
};

export default Insights;
